import React, { useState, useRef } from 'react'
import { useNavigate } from "react-router-dom";
import { HcContext, SizeOfWindow } from '../../types/hccontext';
import { ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import H1Title from '../title/H1Title'
import GetStrLocalized from '../../datas/GetStrLocalized'
import Banner from './Banner';
import InputWithStyle from "../banner/InputWithStyle";


interface BannerCarouselProps {
    language: string;
    hcContext: HcContext;
    searchValue: string;
    useMicrophone: boolean;
}

const BannerCarousel: React.FC<BannerCarouselProps> = ({ language, hcContext, searchValue, useMicrophone }) => {

    let banners = [
        {
            img: 'https://assets.radiorcf.com/styles/890x500/assets/2025-08/hq720.jpg.webp',
            verse: GetStrLocalized(language, "askAndItWillBeGivenToYou")
        },
    ];

    if (language === "fr") {
        banners.push({
            img: '/upload/godly_play_banner.jpg',
            verse: '« Laissez les petits enfants venir à moi » - Matthieu 19,14'
        })
    } else {
        banners.push({
            img: '/upload/jesus_regarde_au_loin.png',
            verse: GetStrLocalized(language, "blessedAreThePeacemakers"),
        })
    }

    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(0);

    const nextSlide = () => {
        setDirection(1);
        setIndex((prev) => (prev + 1) % banners.length);
    };

    const prevSlide = () => {
        setDirection(-1);
        setIndex((prev) => (prev - 1 + banners.length) % banners.length);
    };

    const slideVariants = {
        enter: (dir: number) => ({ x: dir > 0 ? '100%' : '-100%', zIndex: 1 }),
        center: { zIndex: 1, x: '0%' },
        exit: (dir: number) => ({ zIndex: 0, x: dir > 0 ? '-100%' : '100%' }),
    };

    const slideTransition = {
        x: { type: "tween", duration: 0.7, ease: "easeInOut" },
    };

    let navWidth = 250;
    if (hcContext.sizeOfWindow === SizeOfWindow.INTERMEDIARY || hcContext.sizeOfWindow === SizeOfWindow.WIDE) {
        navWidth = hcContext.userWantsNavOpen ? 250 : 60;
    } else if (hcContext.sizeOfWindow === SizeOfWindow.VERY_SHORT) {
        navWidth = hcContext.userWantsNavOpen ? 250 : 0;
    }

    // --- (Styling objects) ---

    const titleStyles: React.CSSProperties = {
        fontSize: '3rem', fontWeight: 800,
        textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
    };

    const navigate = useNavigate();

    const [chatbotQuery, setChatbotQuery] = useState("");
    const chatbotQueryRef = useRef("");
    const chatbotPath = '/' + language + '/' + GetStrLocalized(language, "chatbotFolderName");
    function respondInChatbotPage(inputText: string | undefined) {
        if (inputText && inputText.trim() !== "") {
            const urlizedValue = encodeURIComponent(inputText);
            navigate(chatbotPath + "?q=" + urlizedValue)
        }
    }

    return (
        <>
            <div style={{
                height: '600px',
                overflow: 'hidden',
                position: 'relative',
                marginLeft: `${navWidth}px`,
                transition: 'margin-left 0.3s ease-out'
            }}>
                <div style={{
                    position: 'absolute', inset: '0px', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', textAlign: 'center',
                    color: 'white', paddingTop: '4rem', paddingLeft: '0.5rem', paddingRight: '0.5rem',
                    zIndex: 5, pointerEvents: 'none'
                }}>
                    <H1Title>
                        <span style={titleStyles}>{GetStrLocalized(language, "godSeaksToUs")}</span>
                    </H1Title>
                </div>

                <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                        key={index} custom={direction} variants={slideVariants}
                        initial="enter" animate="center" exit="exit"
                        transition={slideTransition}
                        style={{ position: 'absolute', width: '100%', height: '100%', opacity: 1 }}
                    >
                        <img
                            src={banners[index].img}
                            style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <div style={{ position: 'absolute', inset: '0px', backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 1 }} />

                        <div style={{
                            position: 'absolute', inset: '0px', display: 'flex', flexDirection: 'column',
                            alignItems: 'center', textAlign: 'center',
                            color: 'white', paddingTop: '6rem', paddingLeft: '1rem', paddingRight: '1rem', zIndex: 2
                        }}>
                            <p style={{
                                marginTop: '5rem', fontStyle: 'italic', fontSize: '1.5rem',
                                maxWidth: '42rem', color: 'rgba(255, 255, 255, 0.9)'
                            }}>{banners[index].verse}</p>

                            {index === 0 &&
                                <>
                                    <span style={{ fontSize: '1.2rem', marginTop: '5rem', maxWidth: hcContext.sizeOfWindow != SizeOfWindow.VERY_SHORT ? '42rem' : '20rem' }}>
                                        {GetStrLocalized(language, "whatCanIDoForYouSpiritually")}
                                    </span>
                                    <div style={{
                                        marginTop: '1.5rem',
                                        width: '100%',
                                        maxWidth: '42rem',
                                    }} >
                                        <InputWithStyle onValidation={respondInChatbotPage} value={chatbotQuery}
                                            placeholder={GetStrLocalized(language, "askAnything")}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setChatbotQuery(event.target.value)
                                                chatbotQueryRef.current = event.target.value;
                                            }} language={language} hcContext={hcContext} hasSearchButton={true} height="50px"
                                            useMicrophone={useMicrophone} />
                                    </div>
                                </>
                            }

                            {language === "fr" && index === 1 &&
                                <>
                                    <span style={{ fontSize: '1.2rem', marginTop: '5rem', maxWidth: hcContext.sizeOfWindow != SizeOfWindow.VERY_SHORT ? '42rem' : '20rem' }}>
                                        Découvrez la pédagogie GODLY PLAY :<br />une expérience spirituelle unique !
                                    </span>
                                    <div style={{
                                        marginTop: '1.5rem',
                                        width: '100%',
                                        maxWidth: '42rem',
                                    }} >
                                        <button
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => window.location.href = 'http://godlyplayfrance.fr/'}
                                        >
                                            GODLY PLAY
                                        </button>
                                    </div>
                                </>
                            }



                        </div>
                    </motion.div>
                </AnimatePresence>

                <button onClick={prevSlide} style={{
                    position: 'absolute', left: '1.5rem', top: '50%', transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.4)', color: 'white', padding: '0.75rem',
                    borderRadius: '9999px', zIndex: 8, border: 'none', cursor: 'pointer'
                }}>
                    <ChevronLeft size={36} />
                </button>

                <button onClick={nextSlide} style={{
                    position: 'absolute', right: '1.5rem', top: '50%', transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.4)', color: 'white', padding: '0.75rem',
                    borderRadius: '9999px', zIndex: 8, border: 'none', cursor: 'pointer'
                }}>
                    <ChevronRight size={36} />
                </button>
            </div >

            <Banner language={language} hcContext={hcContext} canDisplaySearchBar={true} searchValue={searchValue} minimum_height_to_scroll={600} />
        </>
    );
};


export default BannerCarousel
